// Detalle de un chofer (Driver) dentro de Liquidaciones > Choferes: mismo
// patrón que ClienteDetailModal.tsx, entidad Driver en vez de Store.
// Reutiliza el mismo OverrideEditModal (Turbo/×2 Doble/Precio manual/CP) —
// la edición financiera de un envío es idéntica, solo cambia a qué
// endpoint se manda. Columnas propias de Driver: Zona (tier), Store
// (informativo, a qué cliente pertenece el envío) y Username LightData.
// Nunca expone pricing_status/override/motivo como texto visible — eso
// solo maneja el ícono de "Pendiente" y el editor.
import React, { useEffect, useMemo, useState } from "react";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { authFetch } from "../../lib/authFetch";
import { downloadAuthenticatedFile } from "../../lib/downloadFile";
import OverrideEditModal, { DeliveryDetail, money } from "./OverrideEditModal";

type ConfigStatus = "CONFIGURADO" | "CON_PENDIENTES" | "SIN_TARIFA";

type DriverSummary = {
  deliveries: number;
  pricedDeliveries: number;
  unresolvedDeliveries: number;
  totalReparto: number;
  premioElite: number | null;
  colecta: number | null;
  totalFinal: number;
};

type DriverDetailResponse = {
  ok: boolean;
  // readOnly (ver informe "Sprint Final — Cierre semanal", Parte 4): true en
  // una semana ya CERRADA — mismo patrón que ClienteDetailModal.tsx.
  period: { fromDisplay: string; toDisplay: string; readOnly?: boolean };
  driver: {
    id: number;
    name: string | null;
    lightdataUsername: string | null;
    vehiculo: string | null;
    tariff: { id: number; name: string } | null;
    configStatus: ConfigStatus | null;
  };
  summary: DriverSummary;
  deliveries: DeliveryDetail[];
};

const CONFIG_BADGE: Record<ConfigStatus, { label: string; color: string }> = {
  CONFIGURADO: { label: "Configurado", color: "var(--success, #2ea043)" },
  CON_PENDIENTES: { label: "Con pendientes", color: "#c99a1f" },
  SIN_TARIFA: { label: "Sin tarifa", color: "var(--danger, #d1242f)" },
};

type Props = {
  open: boolean;
  driverId: number | string | null;
  driverName: string | null;
  token: string | null;
  onClose: () => void;
  // ausente = semana abierta actual; con valor, abre esa semana histórica
  // (ver informe "Sprint Final — Cierre semanal", Parte 3/4).
  periodKey?: string | null;
  // Se llama después de guardar/quitar un override, para que la pantalla
  // que lista todos los choferes también refresque su total.
  onOverrideChanged?: () => void;
};

const overlayStyle: React.CSSProperties = { position: "fixed", inset: 0, zIndex: 120, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" };
const backdropStyle: React.CSSProperties = { position: "absolute", inset: 0, background: "rgba(0,0,0,0.65)" };
const shellStyle: React.CSSProperties = { position: "relative", width: "100%", maxWidth: "1280px", maxHeight: "90vh", overflow: "hidden" };
const headerStyle: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", padding: "18px 20px", borderBottom: "1px solid var(--border)" };
const titleStyle: React.CSSProperties = { margin: 0, fontSize: "18px", fontWeight: 700, color: "var(--text-primary)" };
const subtitleStyle: React.CSSProperties = { margin: "4px 0 0 0", fontSize: "13px", color: "var(--text-secondary)" };
const bodyStyle: React.CSSProperties = { padding: "20px", maxHeight: "76vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: "14px" };
const stateStyle: React.CSSProperties = { padding: "24px 0", color: "var(--text-secondary)", fontSize: "14px", textAlign: "center" };
const tableWrapStyle: React.CSSProperties = { overflowX: "auto", border: "1px solid var(--border)", borderRadius: "14px" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px", color: "var(--text-secondary)", fontSize: "12px", fontWeight: 700, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", background: "rgba(255,255,255,0.02)" };
const tdStyle: React.CSSProperties = { padding: "8px 10px", color: "var(--text-primary)", fontSize: "13px", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" };
const badgeStyle = (color: string): React.CSSProperties => ({ display: "inline-block", padding: "2px 9px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, border: `1px solid ${color}`, color, marginRight: "4px" });
const editButtonStyle: React.CSSProperties = { background: "transparent", border: "1px solid var(--border)", borderRadius: "8px", width: "28px", height: "28px", cursor: "pointer", fontSize: "13px", color: "var(--text-secondary)" };
const searchFieldStyle: React.CSSProperties = { maxWidth: "360px" };

// Badges de excepción: ausencia de badge = cálculo normal, nunca se rotula "NORMAL".
function ExceptionBadges({ d }: { d: DeliveryDetail }) {
  return (
    <>
      {d.is_turbo && <span style={badgeStyle(d.turbo_source === "MANUAL" ? "#c99a1f" : "#1f7ac9")}>⚡ Turbo</span>}
      {d.is_double && <span style={badgeStyle("#1f9c7a")}>×2 Doble</span>}
      {d.manual_price !== null && <span style={badgeStyle("#8a3fd1")}>✏️ Manual</span>}
    </>
  );
}

export default function DriverDetailModal({ open, driverId, driverName, token, onClose, periodKey = null, onOverrideChanged }: Props) {
  const [data, setData] = useState<DriverDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [editing, setEditing] = useState<DeliveryDetail | null>(null);
  const [search, setSearch] = useState("");
  const periodSuffix = periodKey ? `?period=${encodeURIComponent(periodKey)}` : "";
  const readOnly = data?.period?.readOnly === true;

  const loadDetail = React.useCallback(() => {
    if (driverId === null || !token) return;
    setLoading(true);
    setError(null);
    return authFetch(`/v1/liquidaciones/choferes/${driverId}${periodSuffix}`, { token })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "Error al cargar el detalle del chofer");
        setData(payload);
      })
      .catch((e: any) => {
        setError(e.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [driverId, token, periodSuffix]);

  useEffect(() => {
    if (!open || driverId === null || !token) return;
    setData(null);
    setSearch("");
    loadDetail();
  }, [open, driverId, token, loadDetail]);

  // Buscador dentro del detalle: tracking, dirección, destinatario y Store,
  // client-side (los envíos del chofer ya están todos cargados en memoria).
  const filteredDeliveries = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.deliveries;
    return data.deliveries.filter(
      (d) =>
        (d.tracking || "").toLowerCase().includes(q) ||
        (d.direccion || "").toLowerCase().includes(q) ||
        (d.destinatario || "").toLowerCase().includes(q) ||
        (d.store_name || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  if (!open) return null;

  const handleDownload = async () => {
    if (driverId === null) return;
    setDownloading(true);
    try {
      await downloadAuthenticatedFile(
        `/v1/liquidaciones/choferes/${driverId}/export${periodSuffix}`,
        token,
        `liquidacion_${driverName || "chofer"}.xlsx`
      );
    } catch (e: any) {
      alert(e.message || "No se pudo descargar el Excel del chofer.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={backdropStyle} onClick={onClose} />
      <div style={shellStyle}>
        <Card padding="0" style={{ display: "flex", flexDirection: "column" }}>
          <div style={headerStyle}>
            <div>
              <h2 style={titleStyle}>{data?.driver?.name || driverName || "Chofer"}</h2>
              {data && (
                <p style={subtitleStyle}>
                  {data.period.fromDisplay} → {data.period.toDisplay} · {data.deliveries.length} envío(s) · Total {money(data.summary.totalFinal)}
                  {readOnly && <span style={badgeStyle("#c99a1f")}> Semana cerrada · Solo lectura</span>}
                </p>
              )}
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <Button variant="secondary" onClick={handleDownload} disabled={downloading || !data}>
                {downloading ? "Generando…" : "Descargar Excel"}
              </Button>
              <Button variant="secondary" onClick={onClose}>Cerrar</Button>
            </div>
          </div>

          <div style={bodyStyle}>
            {loading && <div style={stateStyle}>Cargando…</div>}
            {!loading && error && <div style={{ ...stateStyle, color: "var(--danger, #d1242f)" }}>{error}</div>}

            {!loading && !error && data && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "10px", padding: "14px 16px", borderRadius: "14px", border: "1px solid var(--border)", background: "var(--surface-alt, transparent)" }}>
                <div>
                  <div style={{ fontSize: "11px", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Username LightData</div>
                  <div style={{ fontSize: "14px", fontWeight: 600 }}>{data.driver.lightdataUsername || "—"}</div>
                </div>
                <div>
                  <div style={{ fontSize: "11px", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Vehículo</div>
                  <div style={{ fontSize: "14px", fontWeight: 600 }}>{data.driver.vehiculo || "Sin informar"}</div>
                </div>
                <div>
                  <div style={{ fontSize: "11px", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Envíos</div>
                  <div style={{ fontSize: "14px", fontWeight: 600 }}>{data.summary.deliveries} ({data.summary.pricedDeliveries} valorizados)</div>
                </div>
                <div>
                  <div style={{ fontSize: "11px", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Total calculado</div>
                  <div style={{ fontSize: "14px", fontWeight: 600 }}>{money(data.summary.totalFinal)}</div>
                </div>
                <div>
                  <div style={{ fontSize: "11px", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Estado</div>
                  <div style={{ marginTop: "2px" }}>
                    {data.driver.configStatus && (
                      <span style={{ display: "inline-block", padding: "2px 9px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, border: `1px solid ${CONFIG_BADGE[data.driver.configStatus].color}`, color: CONFIG_BADGE[data.driver.configStatus].color }}>
                        {CONFIG_BADGE[data.driver.configStatus].label}
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: "11px", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Tarifa actual</div>
                  <div style={{ fontSize: "14px", fontWeight: 600 }}>{data.driver.tariff?.name || "Sin tarifa"}</div>
                </div>
              </div>
            )}
            {/* Liquidaciones es solo lectura para la tarifa a propósito (Sprint
                Operativo, Parte 1): cambiarla es propenso a error y no es el
                lugar — se administra desde Usuarios → Editar Chofer. */}

            {!loading && !error && data && data.deliveries.length === 0 && (
              <div style={stateStyle}>
                {periodKey ? "Sin envíos para este chofer en esta semana." : "Sin envíos para este chofer en la semana actual."}
              </div>
            )}
            {!loading && !error && data && data.deliveries.length > 0 && (
              <>
                <div style={searchFieldStyle}>
                  <Input
                    placeholder="Buscar por tracking, dirección, destinatario o Store..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                {filteredDeliveries.length === 0 ? (
                  <div style={stateStyle}>Ningún envío coincide con la búsqueda.</div>
                ) : (
                  <div style={tableWrapStyle}>
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={thStyle}>Tracking</th>
                          <th style={thStyle}>Store</th>
                          <th style={thStyle}>Destinatario</th>
                          <th style={thStyle}>CP</th>
                          <th style={thStyle}>Zona</th>
                          <th style={thStyle}>Estado</th>
                          <th style={thStyle}>Tarifa</th>
                          <th style={thStyle}>Final</th>
                          <th style={thStyle}></th>
                          {!readOnly && <th style={thStyle}></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDeliveries.map((d) => {
                          const pending = d.final_price === null;
                          return (
                            <tr key={String(d.lightdata_id)} title={pending ? d.pricing_reason || undefined : d.override_observation || undefined}>
                              <td style={tdStyle}>{d.tracking || "—"}</td>
                              <td style={tdStyle}>{d.store_name || "—"}</td>
                              <td style={tdStyle}>{d.destinatario || "Sin informar"}</td>
                              <td style={tdStyle}>{d.cp || "—"}</td>
                              <td style={tdStyle}>{d.tier || "—"}</td>
                              <td style={tdStyle}>{d.estado || "—"}</td>
                              <td style={tdStyle}>{d.tariff_name || "—"}</td>
                              <td style={tdStyle}>
                                {pending ? <span style={{ color: "var(--danger, #d1242f)" }}>Pendiente</span> : money(d.final_price)}
                              </td>
                              <td style={tdStyle}>
                                <ExceptionBadges d={d} />
                              </td>
                              {!readOnly && (
                                <td style={tdStyle}>
                                  <button type="button" style={editButtonStyle} title="Editar precio / Turbo / Doble" onClick={() => setEditing(d)}>
                                    ✎
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </Card>
      </div>

      <OverrideEditModal
        open={editing !== null}
        entityLabel={`Chofer: ${data?.driver?.name || driverName || "—"}`}
        token={token}
        delivery={editing}
        overrideUrl={`/v1/liquidaciones/choferes/${driverId}/envios/${editing?.lightdata_id}/override`}
        onClose={() => setEditing(null)}
        onSaved={(detail) => {
          setData(detail);
          setEditing(null);
          onOverrideChanged?.();
        }}
      />
    </div>
  );
}
